import React, { useCallback, useMemo, useContext, useEffect, useState } from 'react'
import useGroomingVisit from '../../../../../hooks/useGroomingVisit'
import SectionFormContainer from '../../../components/SectionFormContainer'
import GroomingAppointmentWizardContext from '../GroomingAppointmentWizardContext'
import { makeStyles, CircularProgress, Icon, Tooltip, DialogTitle, Button } from '@material-ui/core'
import { gray, purple } from '../../../../../utils/colors'
import {
	FONT_WEIGHTS,
	NON_BREAKING_SPACE,
	PLACEHOLDER_DATE,
	formatNumsToDollars,
	calculatePromoAmountToApply,
	GROOMING_SERVICE_LABELS,
	GROOMING,
	getApplicableAmountToApplyToLineItem
} from '../../../../../utils'
import {
	ButtonBase,
	Dialog,
	DialogContent,
	FormControl,
	FormControlLabel,
	RadioGroup,
	Radio,
	TextField,
	IconButton
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import InfoTooltip from '../../../components/InfoTooltip'
import { MainButton } from '../../../../Button/MainButton'
import PaymentMethodCard from '../../../../Cards/Payments/PaymentMethodCard'
import NoFacilityCardWarning from '../../../../Common/NoFacilityCardWarning'
import DifferentFacilityBookingWarning from '../../../../Common/DifferentFacilityBookingWarning'
import useFacilityPaymentStatus from '../../../../../hooks/useFacilityPaymentStatus'
import CancellationPolicyDialog from '../components/CancellationPolicyDialog'
import DemattingTermsDialog from '../components/DemattingTermsDialog'
import moment from 'moment'
import { showError } from '../../../../../actions/misc/misc.actions'
import PromoOnlyEntryDialog from '../../../../Dialogs/Promos/PromoOnlyEntryDialog'
import usePromotions from '../../../../../hooks/usePromotions'
import * as Constants from '../../../../../utils/constants'
import useSelectedHousehold from '../../../../../hooks/useSelectedHousehold'
import StyledAlert from '../../../../Common/StyledAlert'

const useStyles = makeStyles(() => ({
	root: {
		flex: 1,
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'center',
		gap: '1rem',
		padding: '0 1rem'
	},
	invoiceContainer: {
		display: 'flex',
		backgroundColor: gray[300],
		width: '60%',
		borderRadius: '0.375rem',
		flexDirection: 'column',
		padding: '1.4rem'
	},
	invoiceHeader: {
		marginBottom: '1.5rem'
	},
	emphasizedText: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '1rem',
		color: gray[900]
	},
	actionsContainer: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		'& .MuiCard-root': {
			boxShadow: 'none',
			'& .MuiCardHeader-root': {
				'& .MuiCardHeader-content': {
					'& .MuiCardHeader-title': {
						textTransform: 'unset'
					}
				}
			}
		}
	},
	actionButtonContainer: {
		display: 'flex',
		flexDirection: 'column',
		padding: '1rem',
		gap: '1rem'
	},
	disclaimerText: {
		textAlign: 'center',
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		padding: '0 0.5rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& span': {
			fontWeight: FONT_WEIGHTS.ExtraBold,
			color: purple[500],
			cursor: 'pointer'
		}
	},
	activityOverlay: {
		position: 'absolute',
		zIndex: 200,
		backgroundColor: 'rgba(255,255,255,.8)',
		width: '100%',
		height: '100%',
		display: 'flex',
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center'
	},
	spacer: {
		flex: 1,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'& div:nth-child(1)': {
			height: '1px',
			borderBottom: `1px solid ${gray[500]}`,
			flex: 1
		}
	},
	infoButtonIcon: {
		color: purple[500],
		fontSize: '1.18rem',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		overflow: 'unset'
	},
	changeButton: {
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		color: purple[500]
	},
	inlineButtons: {
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		color: purple[500]
	},
	otherContainer: {
		display: 'flex',
		'& .MuiTextField-root': {
			minWidth: 'unset',
			flex: 1
		},
		'& .MuiTextField-root .MuiFilledInput-underline': {
			minWidth: 'unset',
			flex: 1
		}
	},
	tipDialogContent: { display: 'flex', flexDirection: 'column' },
	tipDialogHeader: {
		display: 'flex',
		marginBottom: '.8rem',
		'& div:nth-child(1)': {
			flex: 1
		}
	},
	tipDialogActions: { display: 'flex', justifyContent: 'space-between' },
	groomerTipDisplay: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
	radioGroup: {
		'& .MuiFormGroup-root *.MuiFormControlLabel-root *.MuiIconButton-label *.MuiSvgIcon-root': {
			fill: purple[900],
			color: purple[900]
		}
	},
	addPromoButton: {
		color: purple[500],
		fontSize: '0.875rem',
		letterSpacing: 1.25,
		padding: 0,
		marginTop: '1.25rem'
	},
	subtext: {
		fontSize: '.875rem',
		color: gray[700],
		fontStyle: 'italic'
	}
}))

const useTableStyles = makeStyles(() => ({
	row: {
		display: 'flex',
		flex: 1,
		gap: '2rem',
		alignItems: 'center',
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.Medium,
		fontSize: '1rem',
		color: gray[900],
		'& div:nth-child(1)': {
			flex: 1
		},
		'& div:nth-child(2)': {
			width: '2.5rem'
		},
		'& div:nth-child(3)': {
			flex: 1
		},
		'& div:nth-child(4)': {
			width: '1.5rem'
		}
	},
	header: {
		borderBottom: `1px solid ${gray[500]}`,
		height: '2rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		fontSize: '0.875rem',
		flex: 'unset',
		marginBottom: '0.3125rem'
	},
	table: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.4rem'
	}
}))

const Row = ({ children, header = false }) => {
	const classes = useTableStyles()
	return <div className={`${classes.row} ${header ? classes.header : null}`}>{children}</div>
}

const Header = ({ children }) => {
	return <Row header>{children}</Row>
}

const FakeTable = ({ children }) => {
	const classes = useTableStyles()
	return <div className={classes.table}>{children}</div>
}

const ServiceSummaryAndPayment = (props) => {
	const {
		wizardContextProvider: { grooming_appointment, setWizardAttributeAndValues }
	} = useContext(GroomingAppointmentWizardContext)
	const classes = useStyles()
	const {
		facility,
		getPriceAndDuration,
		fetchingAvailability,
		updateAddOns,
		confirmAndBook,
		getTimesIn24HourFormat,
		groomingCreditTotal: groomingCreditTotalFromHook,
		groomingRates
	} = useGroomingVisit()
	const { deletePromoForFacilityVisit } = usePromotions()

	const [groomerTipPercent, setGroomerTipPercent] = useState('18')
	const [groomerTipDiscreteAmount, setGroomerTipDiscreteAmount] = useState('')
	const [tempTipPercent, setTempTipPercent] = useState('18')
	const [tempTipDiscreteAmount, setTempTipDiscreteAmount] = useState('')
	const [editTip, setEditTip] = useState(false)
	const [demattingPolicyOpen, setDemattingPolicyOpen] = useState(false)
	const [cancellationPolicyOpen, setCancellationPolicyOpen] = useState(false)
	const [promoEntryDialogOpen, setPromoEntryDialogOpen] = useState(false)
	const [showBankCreditDisclaimer, setShowBankCreditDisclaimer] = useState(false)

	const { addOnBankValue, hasValidCardOnFile, household, totalCreditAvailable } = useSelectedHousehold()

	// Cards are saved per facility. The wizard creates a Stripe customer for this
	// facility and will not confirm until a card exists on that facility's instance.
	const facilityCardStatus = useFacilityPaymentStatus(household?._id, facility?._id, { ensureCustomer: true })
	const hasCardAtThisFacility =
		facilityCardStatus.hasPaymentMethod === true ||
		(facilityCardStatus.hasPaymentMethod == null &&
			String(facility?._id || '') === String(household?.default_facility_id || '') &&
			hasValidCardOnFile)

	const handleEditTipDialogState = useCallback(() => {
		setEditTip((prev) => !prev)
	}, [setEditTip])

	const handleDemattingPolicyDialogState = useCallback(() => {
		setDemattingPolicyOpen((prev) => !prev)
	}, [setDemattingPolicyOpen])

	const handleCancellationPolicyDialogState = useCallback(() => {
		setCancellationPolicyOpen((prev) => !prev)
	}, [setCancellationPolicyOpen])

	const handlePromoEntryDialogState = useCallback(() => {
		setPromoEntryDialogOpen((prev) => !prev)
	}, [setPromoEntryDialogOpen])

	const handleConfirmTipChange = useCallback(() => {
		if (tempTipPercent !== 'other') {
			setGroomerTipPercent(tempTipPercent)
			setGroomerTipDiscreteAmount('')
		} else {
			setGroomerTipPercent(tempTipPercent)
			setGroomerTipDiscreteAmount(tempTipDiscreteAmount)
		}
		handleEditTipDialogState()
	}, [setGroomerTipDiscreteAmount, setGroomerTipPercent, tempTipPercent, tempTipDiscreteAmount])

	const handleTipPercentChange = useCallback(
		(e) => {
			const value = e.target.value
			setTempTipPercent(value)
			if (value !== 'other') {
				setTempTipDiscreteAmount('')
			}
		},
		[setTempTipPercent, setTempTipDiscreteAmount]
	)

	const handleDiscreteTipEntry = useCallback(
		(e) => {
			const regex = /(^0+)|\D+/gi
			setTempTipDiscreteAmount(e.target.value.replaceAll(regex, ''))
		},
		[setTempTipDiscreteAmount]
	)

	const handleRemoveAddOn = useCallback(
		async (name) => {
			const newValues = { ...grooming_appointment }
			let newAddOnArray = []
			const hasAddon = grooming_appointment['add_on_services'].findIndex((a) => a.name === name)
			if (hasAddon > -1) {
				newAddOnArray = newValues['add_on_services'].filter((a) => a.name !== name)
				newValues['add_on_services'] = newAddOnArray
				setWizardAttributeAndValues(newValues)
				await updateAddOns(newValues['add_on_services'])
			}
		},
		[grooming_appointment, setWizardAttributeAndValues]
	)

	const handleRemovePromo = useCallback(async () => {
		const { applied_promo, _id, household_id } = grooming_appointment
		if (!applied_promo) {
			return
		}

		await deletePromoForFacilityVisit({
			promo_code: applied_promo.code,
			promo_id: applied_promo._id,
			visit_id: _id,
			household_id
		})
	}, [deletePromoForFacilityVisit, grooming_appointment])

	const fetchPriceAndDuration = useCallback(async () => {
		try {
			const res = await getPriceAndDuration(grooming_appointment)
			if (res) {
				const newValues = { ...grooming_appointment, ...res }
				setWizardAttributeAndValues(newValues)
			}
		} catch (e) {
			const resetValues = {
				...grooming_appointment,
				priceMin: -1,
				priceMax: -1,
				duration: -1
			}
			setWizardAttributeAndValues(resetValues)
		}
	}, [grooming_appointment, getPriceAndDuration, setWizardAttributeAndValues])

	useEffect(() => {
		const { priceMin, priceMax } = grooming_appointment
		if (priceMin < 0 || priceMax < 0) {
			fetchPriceAndDuration()
		}
	}, [])

	const serviceLineItem = useMemo(() => {
		const { priceMax, priceMin, grooming_service_type } = grooming_appointment
		return [
			{
				description: GROOMING_SERVICE_LABELS[grooming_service_type],
				quantity: '1x',
				priceMax,
				priceMin,
				price: priceMax,
				formattedPrice: `${formatNumsToDollars(priceMin)}`
			}
		]
	}, [grooming_appointment])

	const addOnlineItems = useMemo(() => {
		const items = []
		const { add_on_services } = grooming_appointment

		if (facility) {
			const { addOnTypes } = facility || []

			add_on_services.forEach((addon) => {
				let price = -1
				const {
					name,
					_id: rawId,
					addon_id: existingAddOnId,
					price: localPrice,
					addon_choice_id = ''
				} = addon

				const masterAddon = addOnTypes.find(({ _id, name }) => {
					return _id === existingAddOnId || _id === rawId
				})

				if (localPrice) {
					price = localPrice
				} else {
					if (masterAddon) {
						price = masterAddon.price
						if (addon_choice_id && masterAddon.addon_choices) {
							const { upcharge } = masterAddon.addon_choices.find((choice) => {
								return choice._id === addon_choice_id
							}) || { upcharge: 0 }

							price += upcharge
						}
					}
				}

				let formattedPrice = price > -1 ? `${formatNumsToDollars(price)}` : '??'

				const priceMax = masterAddon?.max_price || 0
				const priceMin = masterAddon?.min_price || 0

				if (priceMax > 0 && priceMin > 0) {
					formattedPrice = `${formatNumsToDollars(priceMin)} - ${formatNumsToDollars(priceMax)}`
				}

				items.push({
					description: name,
					quantity: '1x',
					priceMax,
					priceMin,
					price,
					formattedPrice,
					onRemove: () => handleRemoveAddOn(name)
				})
			})
		}

		return items
	}, [grooming_appointment, facility, handleRemoveAddOn])

	const subTotalValues = useMemo(() => {
		let minTotal = 0
		let maxTotal = 0
		let addonMinTotal = 0
		let addonMaxTotal = 0
		let serviceLineMinTotal = 0
		let serviceLineMaxTotal = 0
		let serviceLineTotal = 0

		for (const item of addOnlineItems) {
			const { priceMax, priceMin, price } = item
			if (priceMax === 0 && priceMin === 0) {
				addonMinTotal += price
				addonMaxTotal += price
			} else {
				addonMinTotal += priceMin
				addonMaxTotal += priceMax
			}
		}

		for (const item of serviceLineItem) {
			const { priceMax, priceMin, price } = item
			if (priceMax === -1 && priceMin === -1) {
				serviceLineMinTotal += price
				serviceLineMaxTotal += price
				serviceLineTotal += price
				maxTotal += price + addonMaxTotal
				minTotal += price + addonMinTotal
			} else {
				serviceLineMinTotal += priceMin
				serviceLineMaxTotal += priceMax
				serviceLineTotal += price
				maxTotal += priceMax + addonMaxTotal
				minTotal += priceMin + addonMinTotal
			}
		}


		return {
			maxTotal,
			minTotal,
			addonMaxTotal,
			addonMinTotal,
			serviceLineTotal,
			serviceLineMaxTotal,
			serviceLineMinTotal
		}
	}, [serviceLineItem, addOnlineItems])

	const subTotalValuesWithPromoAndAddonBank = useMemo(() => {
		const { applied_promo } = grooming_appointment
		const { addonMaxTotal, addonMinTotal, serviceLineMaxTotal, serviceLineMinTotal } = subTotalValues

		let minAmountToApply = 0
		let maxAmountToApply = 0
		let minPromoAmountAppliedToAddOnsInCents = 0
		let maxPromoAmountAppliedToAddOnsInCents = 0
		let minAddonBankUsed = 0
		let maxAddonBankUsed = 0
		let remainingMinAddonBalance = addonMinTotal
		let remainingMaxAddonBalance = addonMaxTotal
		let remainingAddonBankMin = addOnBankValue || 0
		let remainingAddonBankMax = addOnBankValue || 0
		let addonBankAppliedInCentsMin = 0
		let addonBankAppliedInCentsMax = 0

		if (!applied_promo && addOnBankValue <= 0) {
			return {
				minAmountToApply,
				maxAmountToApply,
				minAddonBankUsed,
				maxAddonBankUsed,
				minPromoAmountAppliedToAddOnsInCents,
				maxPromoAmountAppliedToAddOnsInCents,
				addonBankAppliedInCentsMax,
				addonBankAppliedInCentsMin,
				noPromoOrBank: true
			}
		}

		const { code, value, unit, applicable_services = [] } = applied_promo || {}

		// Need to handle $ based promos
		const promoIsCentBased = unit === Constants.CENTS
		const promoValue = value || 0

		let minRemainingPromoAmountInCents = promoIsCentBased ? promoValue : -1
		let maxRemainingPromoAmountInCents = promoIsCentBased ? promoValue : -1

		const appliesToGrooming = applicable_services.includes(GROOMING)

		if (appliesToGrooming) {
			let minAmountApplicableToPromo = serviceLineMinTotal
			let maxAmountApplicableToPromo = serviceLineMaxTotal
			const applyGroomingCreditToPromo =
				groomingCreditTotalFromHook > 0 &&
				(serviceLineMinTotal > 0 || serviceLineMaxTotal > 0)
			const groomingBathPrice = groomingRates?.[grooming_appointment?.pets?.[0]?._id]?.bath || 0

			const minToApplyToService = calculatePromoAmountToApply({
				promo: applied_promo,
				total: minAmountApplicableToPromo,
				visit: grooming_appointment,
				applyGroomingCredit: applyGroomingCreditToPromo,
				groomingBathPrice
			})

			const maxToApplyToService = calculatePromoAmountToApply({
				promo: applied_promo,
				total: maxAmountApplicableToPromo,
				visit: grooming_appointment,
				applyGroomingCredit: applyGroomingCreditToPromo,
				groomingBathPrice
			})

			minPromoAmountAppliedToAddOnsInCents += -minToApplyToService
			maxPromoAmountAppliedToAddOnsInCents += -maxToApplyToService
			if (promoIsCentBased) {
				minRemainingPromoAmountInCents += minToApplyToService
				maxRemainingPromoAmountInCents += maxToApplyToService
			}
		}

		if (addOnlineItems.length > 0) {
			if (promoIsCentBased) {
				if (promoValue > 0) {
					if (minRemainingPromoAmountInCents > 0) {
						const applicableAmountMin = getApplicableAmountToApplyToLineItem(
							addonMinTotal,
							minRemainingPromoAmountInCents,
							minPromoAmountAppliedToAddOnsInCents
						)

						remainingMinAddonBalance -= applicableAmountMin.individualAmountApplied
						minRemainingPromoAmountInCents = applicableAmountMin.remainingBalance
						minPromoAmountAppliedToAddOnsInCents = applicableAmountMin.runningAmountApplied
					}
					if (maxRemainingPromoAmountInCents > 0) {
						const applicableAmountMax = getApplicableAmountToApplyToLineItem(
							addonMaxTotal,
							maxRemainingPromoAmountInCents,
							maxPromoAmountAppliedToAddOnsInCents
						)

						remainingMaxAddonBalance -= applicableAmountMax.individualAmountApplied
						maxRemainingPromoAmountInCents = applicableAmountMax.remainingBalance
						maxPromoAmountAppliedToAddOnsInCents = applicableAmountMax.runningAmountApplied
					}
				}
			} else {
				const percentToInt = parseInt(promoValue, 10)
				const minSubtractedAmount = addonMinTotal * (percentToInt / 100)
				const maxSubtractedAmount = addonMaxTotal * (percentToInt / 100)

				remainingMinAddonBalance -= minSubtractedAmount
				remainingMaxAddonBalance -= maxSubtractedAmount
				minPromoAmountAppliedToAddOnsInCents += minSubtractedAmount
				maxPromoAmountAppliedToAddOnsInCents += maxSubtractedAmount
				minRemainingPromoAmountInCents = -1
				maxRemainingPromoAmountInCents = -1
			}
		}

		if (remainingAddonBankMin > 0 && remainingMinAddonBalance > 0) {
			const applicableBankAmountMin = getApplicableAmountToApplyToLineItem(
				remainingMinAddonBalance,
				remainingAddonBankMin,
				addonBankAppliedInCentsMin
			)

			remainingMinAddonBalance -= applicableBankAmountMin.individualAmountApplied
			remainingAddonBankMin -= applicableBankAmountMin.remainingBalance
			addonBankAppliedInCentsMin = applicableBankAmountMin.runningAmountApplied
			setShowBankCreditDisclaimer(true)
		}

		if (remainingAddonBankMax > 0 && remainingMaxAddonBalance > 0) {
			const applicableBankAmountMax = getApplicableAmountToApplyToLineItem(
				remainingMaxAddonBalance,
				remainingAddonBankMax,
				addonBankAppliedInCentsMax
			)

			remainingMaxAddonBalance -= applicableBankAmountMax.individualAmountApplied
			remainingAddonBankMax -= applicableBankAmountMax.remainingBalance
			addonBankAppliedInCentsMax = applicableBankAmountMax.runningAmountApplied
			setShowBankCreditDisclaimer(true)
		}

		return {
			minAmountToApply,
			maxAmountToApply,
			minPromoAmountAppliedToAddOnsInCents,
			maxPromoAmountAppliedToAddOnsInCents,
			addonBankAppliedInCentsMin,
			addonBankAppliedInCentsMax
		}
	}, [subTotalValues, grooming_appointment, addOnBankValue, setShowBankCreditDisclaimer])

	const groomingCreditLineItems = useMemo(() => {
		if (groomingCreditTotalFromHook <= 0) {
			return []
		}

		const { priceMin = 0, priceMax = 0 } = grooming_appointment || {}
		const groomingServicePrice = priceMin
		if (groomingServicePrice <= 0 && priceMax <= 0) {
			return []
		}

		const pet_id = grooming_appointment?.pets?.[0]?._id
		const groomingRate = groomingRates?.[pet_id]?.bath
		if (!groomingRate) {
			return []
		}
		const price = -groomingRate

		return [
			{
				description: '1x Grooming Credit',
				quantity: '1x',
				priceMax: price,
				priceMin: price,
				price: price,
				formattedPrice: `-${formatNumsToDollars(price)}`,
			}

		]
	}, [groomingCreditTotalFromHook, grooming_appointment, groomingRates])

	const groomingCreditTotal = useMemo(() => {
		return groomingCreditLineItems.reduce((acc, item) => acc + item.price, 0) || 0
	}, [groomingCreditLineItems])

	const availableCreditLineItems = useMemo(() => {
		if (totalCreditAvailable <= 0) {
			return []
		}

		const { minTotal, maxTotal } = subTotalValues
		const {
			minPromoAmountAppliedToAddOnsInCents,
			maxPromoAmountAppliedToAddOnsInCents,
			addonBankAppliedInCentsMin,
			addonBankAppliedInCentsMax
		} = subTotalValuesWithPromoAndAddonBank

		let min = minTotal - minPromoAmountAppliedToAddOnsInCents - addonBankAppliedInCentsMin
		let max = maxTotal - maxPromoAmountAppliedToAddOnsInCents - addonBankAppliedInCentsMax

		let minCreditToApply = 0
		let maxCreditToApply = 0

		if (totalCreditAvailable >= min) {
			minCreditToApply = -min
		} else if (totalCreditAvailable < min) {
			minCreditToApply = -totalCreditAvailable
		}

		if (totalCreditAvailable >= max) {
			maxCreditToApply = -max
		} else if (totalCreditAvailable < max) {
			maxCreditToApply = -totalCreditAvailable
		}

		setShowBankCreditDisclaimer(true)

		const items = [
			{
				description: 'credit',
				quantity: '',
				priceMax: maxCreditToApply,
				priceMin: minCreditToApply,
				price: maxCreditToApply,
				formattedPrice: `${formatNumsToDollars(minCreditToApply)} - ${formatNumsToDollars(
					maxCreditToApply
				)}`
			}
		]

		return items
	}, [totalCreditAvailable, subTotalValues, subTotalValuesWithPromoAndAddonBank, setShowBankCreditDisclaimer])

	const formattedAddonPrice = useMemo(() => {
		const { addonBankAppliedInCentsMin, addonBankAppliedInCentsMax } = subTotalValuesWithPromoAndAddonBank
		if (addonBankAppliedInCentsMin === addonBankAppliedInCentsMax) {
			return `(-${formatNumsToDollars(addonBankAppliedInCentsMin)})`
		}

		return `-(${formatNumsToDollars(addonBankAppliedInCentsMin)} - ${formatNumsToDollars(
			addonBankAppliedInCentsMax
		)})`
	}, [subTotalValuesWithPromoAndAddonBank])

	const totalsWithTip = useMemo(() => {
		const { minTotal, maxTotal } = subTotalValues
		const {
			minPromoAmountAppliedToAddOnsInCents,
			maxPromoAmountAppliedToAddOnsInCents,
			addonBankAppliedInCentsMin,
			addonBankAppliedInCentsMax
		} = subTotalValuesWithPromoAndAddonBank

		const { priceMax: maxCreditToApply, priceMin: minCreditToApply } = availableCreditLineItems[0] || {
			priceMin: 0,
			priceMax: 0
		}

		const discreteTip = groomerTipDiscreteAmount > '' ? parseInt(groomerTipDiscreteAmount, 10) * 100 : 0

		const tipAmounts = {
			15: {
				min: Math.round(minTotal * 0.15),
				max: Math.round(maxTotal * 0.15)
			},
			18: {
				min: Math.round(minTotal * 0.18),
				max: Math.round(maxTotal * 0.18)
			},
			20: {
				min: Math.round(minTotal * 0.2),
				max: Math.round(maxTotal * 0.2)
			},
			23: {
				min: Math.round(minTotal * 0.23),
				max: Math.round(maxTotal * 0.23)
			},
			other: {
				min: discreteTip,
				max: discreteTip
			}
		}

		// I wrote this, but its a mess. Some values are positive integers and some are negative.
		const totalWithTips = {
			15: {
				min:
					minTotal -
					minPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMin +
					minCreditToApply +
					tipAmounts[15].min,
				max:
					maxTotal -
					maxPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMax +
					maxCreditToApply +
					tipAmounts[15].max
			},
			20: {
				min:
					minTotal -
					minPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMin +
					minCreditToApply +
					tipAmounts[20].min,
				max:
					maxTotal -
					maxPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMax +
					maxCreditToApply +
					tipAmounts[20].max
			},
			23: {
				min:
					minTotal -
					minPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMin +
					minCreditToApply +
					tipAmounts[23].min,
				max:
					maxTotal -
					maxPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMax +
					maxCreditToApply +
					tipAmounts[23].max
			},
			18: {
				min:
					minTotal -
					minPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMin +
					minCreditToApply +
					tipAmounts[18].min,
				max:
					maxTotal -
					maxPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMax +
					maxCreditToApply +
					tipAmounts[18].max
			},
			other: {
				min:
					minTotal -
					minPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMin +
					minCreditToApply +
					discreteTip,
				max:
					maxTotal -
					maxPromoAmountAppliedToAddOnsInCents -
					addonBankAppliedInCentsMax +
					maxCreditToApply +
					discreteTip
			}
		}


		return {
			tipAmounts,
			totalWithTips,
			tip: tipAmounts[groomerTipPercent],
			total: totalWithTips[groomerTipPercent]
		}
	}, [
		subTotalValues,
		groomerTipPercent,
		groomerTipDiscreteAmount,
		subTotalValuesWithPromoAndAddonBank,
		groomingCreditLineItems,
		availableCreditLineItems
	])

	const hasVariablePrice = useMemo(() => {
		if (serviceLineItem) {
			const [service] = serviceLineItem
			if (service.priceMax > service.priceMin) {
				return true
			}
		}
		if (addOnlineItems && addOnlineItems.length > 0) {
			let variablePricedLineItem = false
			for (const item of addOnlineItems) {
				if (item.priceMax > item.priceMin) {
					variablePricedLineItem = true
					break
				}
			}
			return variablePricedLineItem
		}
	}, [serviceLineItem, addOnlineItems])

	const appliedPromosLineItems = useMemo(() => {
		const { applied_promo } = grooming_appointment

		const lineItems = []

		if (!applied_promo) {
			return lineItems
		}

		const { code, value } = applied_promo
		const { minPromoAmountAppliedToAddOnsInCents, maxPromoAmountAppliedToAddOnsInCents } =
			subTotalValuesWithPromoAndAddonBank

		let formattedPrice = `${formatNumsToDollars(-minPromoAmountAppliedToAddOnsInCents)}`

		if (minPromoAmountAppliedToAddOnsInCents !== maxPromoAmountAppliedToAddOnsInCents) {
			formattedPrice = `${formatNumsToDollars(
				-minPromoAmountAppliedToAddOnsInCents
			)} - ${formatNumsToDollars(-maxPromoAmountAppliedToAddOnsInCents)}`
		}

		lineItems.push({
			description: (
				<div>
					Applied Promo
					<br />
					<span className={classes.subtext}>{code}</span>
				</div>
			),
			tooltipDescription: code,
			quantity: 1,
			upcharge: 0,
			price: value,
			totalPrice: value,
			formattedQuantity: '',
			formattedUpcharge: '',
			formattedPrice,
			formattedTotalPrice: '',
			onRemove: () => handleRemovePromo()
		})
		return lineItems
	}, [grooming_appointment, subTotalValuesWithPromoAndAddonBank])

	const handleOnConfirmAndBook = useCallback(async () => {
		const {
			_id,
			dates,
			matting_terms_accepted,
			duration,
			preferred_staff_id,
			isRebook,
			add_on_services,
			dropoff_time: unformattedDropoffTime,
			staff_id
		} = grooming_appointment
		const formattedDate = moment(dates[0], 'YYYY-MM-DD').hour(12).toDate()
		const { pickup_time, pickup_end_time, dropoff_time } = getTimesIn24HourFormat({
			dropoff_time: unformattedDropoffTime,
			duration
		})

		const { priceMin, priceMax, specificationsUsedInPricing } = grooming_appointment

		const updatedValues = {
			tip_percent: parseInt(groomerTipPercent, 10),
			quoted_price_max: priceMax,
			quoted_price_min: priceMin,
			matting_terms_accepted,
			startDate: formattedDate,
			endDate: formattedDate,
			visit_date: formattedDate,
			duration,
			staff_id,
			dates,
			dropoff_time,
			start_time: dropoff_time,
			pickup_time,
			pickup_end_time,
			specificationsUsedInPricing
		}
		if (preferred_staff_id > '' && preferred_staff_id !== 'none') {
			updatedValues['preferred_staff_id'] = preferred_staff_id
			updatedValues['staff_id'] = preferred_staff_id
		}
		if (groomerTipDiscreteAmount > '') {
			const tipAmountToCents = parseInt(groomerTipDiscreteAmount, 10) * 100
			updatedValues['tip_percent'] = 0
			updatedValues['tip'] = tipAmountToCents
			updatedValues['manual_tip_amount'] = tipAmountToCents
		}

		updatedValues.require_facility_payment_method = true

		if (isRebook) {
			updatedValues['add_on_services'] = [...add_on_services]
			updatedValues['add_on_services'].forEach((addon) => {
				if (addon.date === PLACEHOLDER_DATE) {
					addon.date = dates[0]
				}
			})
		}

		try {
			const response = await confirmAndBook({ visitId: _id, updates: updatedValues })
			if (response && response.data) {
				const { data } = response
				if (data.visit && props.onComplete) {
					props.onComplete()
				}
			}
		} catch (e) {
			showError('Error Scheduling!')
			console.log('error scheduling', e)
		}
	}, [grooming_appointment, totalsWithTip, props, getTimesIn24HourFormat, confirmAndBook])

	return (
		<SectionFormContainer>
			<CancellationPolicyDialog
				open={cancellationPolicyOpen}
				onClose={handleCancellationPolicyDialogState}
			/>
			<PromoOnlyEntryDialog
				open={promoEntryDialogOpen}
				onCancel={handlePromoEntryDialogState}
				onComplete={handlePromoEntryDialogState}
				visit={grooming_appointment}
			/>
			<DemattingTermsDialog open={demattingPolicyOpen} onClose={handleDemattingPolicyDialogState} />
			<Dialog
				PaperProps={{
					style: {
						width: 400,
						maxWidth: 400,
						minHeight: 400,
						minWidth: 400
					}
				}}
				maxWidth="md"
				open={editTip}
				onClose={handleEditTipDialogState}>
				<DialogTitle>
					<div className={classes.tipDialogHeader}>
						<div>Groomer Tip</div>
						<IconButton disableRipple style={{ padding: 0, flex: 0 }}>
							<Close onClick={handleEditTipDialogState} />
						</IconButton>
					</div>
				</DialogTitle>
				<DialogContent className={classes.tipDialogContent}>
					<FormControl className={classes.radioGroup} component="fieldset" style={{ flex: 1 }}>
						<RadioGroup
							aria-label="tip"
							name="tip"
							value={`${tempTipPercent}`}
							onChange={handleTipPercentChange}>
							{Object.entries(totalsWithTip.tipAmounts).map(([key, value]) => {
								if (key !== 'other') {
									return (
										<FormControlLabel
											value={key}
											label={`${key}% (${formatNumsToDollars(
												value.min
											)} - ${formatNumsToDollars(value.max)})`}
											control={<Radio />}
										/>
									)
								}
							})}
							<div className={classes.otherContainer}>
								<FormControlLabel value={'other'} label={'Other'} control={<Radio />} />
								<TextField
									disabled={tempTipPercent !== 'other'}
									label="Other Amount in dollars"
									value={tempTipDiscreteAmount}
									variant="filled"
									onChange={handleDiscreteTipEntry}
								/>
							</div>
						</RadioGroup>
					</FormControl>
					<div className={classes.tipDialogActions}>
						<MainButton variant="text" onClick={handleEditTipDialogState} label="Cancel" />
						<MainButton variant="contained" onClick={handleConfirmTipChange} label="Confirm" />
					</div>
				</DialogContent>
			</Dialog>
			<div className={classes.root}>
				{fetchingAvailability ? (
					<div className={classes.activityOverlay}>
						<CircularProgress />
					</div>
				) : null}
				<div className={classes.invoiceContainer}>
					<div className={`${classes.invoiceHeader} ${classes.emphasizedText}`}>
						Grooming Service Details
					</div>
					<FakeTable>
						<Header>
							<div>ITEM</div>
							<div>QTY</div>
							<div>COST</div>
							<div>{NON_BREAKING_SPACE}</div>
						</Header>
						{[...serviceLineItem, ...groomingCreditLineItems, ...addOnlineItems, ...appliedPromosLineItems].map(
							({ description, quantity, formattedPrice, onRemove, tooltipDescription }) => {
								return (
									<Row>
										<div>{description}</div>
										<div>{quantity}</div>
										<div>{formattedPrice}</div>
										{onRemove ? (
											<div>
												<Tooltip
													title={`Remove ${tooltipDescription || description}`}
													placement="right">
													<ButtonBase onClick={onRemove}>
														<Close
															style={{
																color: gray[700],
																fontSize: '1.5rem',
																fontWeight: FONT_WEIGHTS.ExtraBold
															}}
														/>
													</ButtonBase>
												</Tooltip>
											</div>
										) : (
											<div>{NON_BREAKING_SPACE}</div>
										)}
									</Row>
								)
							}
						)}
						{addOnlineItems.length > 0 && addOnBankValue > 0 ? (
							<Row>
								<div>Enrichment Bank</div>
								<div>{NON_BREAKING_SPACE}</div>
								<div>{formattedAddonPrice}</div>
								<div>{NON_BREAKING_SPACE}</div>
							</Row>
						) : null}
					</FakeTable>
					<div className={classes.spacer}>
						<div />
					</div>
					<FakeTable>
						<Row>
							<div>Groomer Tip</div>
							<div>{NON_BREAKING_SPACE}</div>
							<div className={classes.groomerTipDisplay}>
								{`${groomerTipPercent !== 'other' ? `${groomerTipPercent}%` : 'Other'}`}{' '}
								<ButtonBase className={classes.changeButton} onClick={handleEditTipDialogState}>
									Change
								</ButtonBase>
							</div>
							<div>{NON_BREAKING_SPACE}</div>
						</Row>
						<Row>
							<div>Applied Tip</div>
							<div>{NON_BREAKING_SPACE}</div>
							<div>
								{totalsWithTip.tip.max > totalsWithTip.tip.min ? (
									<span>
										{formatNumsToDollars(totalsWithTip.tip.min)} -{' '}
										{formatNumsToDollars(totalsWithTip.tip.max)}
									</span>
								) : (
									<span>{formatNumsToDollars(totalsWithTip.tip.min)}</span>
								)}
							</div>
							<div>
								<InfoTooltip
									title={`100% of your tip goes to your groomer, and you can change/edit your tip up until the night that you pick your dog up (after grooming appointment).`}
									placement="right">
									<ButtonBase>
										<Icon className={`fas fa-info-circle ${classes.infoButtonIcon}`} />
									</ButtonBase>
								</InfoTooltip>
							</div>
						</Row>
					</FakeTable>
					<div className={classes.spacer}>
						<div />
					</div>
					{availableCreditLineItems.length > 0 ? (
						<>
							<FakeTable>
								<Row>
									<div>Account Credit</div>
									<div>{NON_BREAKING_SPACE}</div>
									<div>{availableCreditLineItems[0].formattedPrice}</div>
									<div>{NON_BREAKING_SPACE}</div>
								</Row>
							</FakeTable>
							<div className={classes.spacer}>
								<div />
							</div>
						</>
					) : null}
					<FakeTable>
						<Row>
							<div className={classes.emphasizedText}>Quoted Service Price</div>
							<div>{NON_BREAKING_SPACE}</div>
							<div className={classes.emphasizedText}>
								{formatNumsToDollars(totalsWithTip.total.min + groomingCreditTotal)}
							</div>
							{hasVariablePrice ? (
								<div>
									<InfoTooltip
										title={`The total amount due is shown as a range, because you have one or more items in your invoice that have a variable price. The total amount due will be set by the end of the appointment, and it will be within the range.`}
										placement="right">
										<ButtonBase>
											<Icon className={`fas fa-info-circle ${classes.infoButtonIcon}`} />
										</ButtonBase>
									</InfoTooltip>
								</div>
							) : (
								<div>{NON_BREAKING_SPACE}</div>
							)}
						</Row>
					</FakeTable>
					<div>
						<Button
							onClick={handlePromoEntryDialogState}
							className={classes.addPromoButton}
							disabled={grooming_appointment['applied_promo'] !== null}
							variant="text">
							Add Promo
						</Button>
					</div>
				</div>
				<div className={classes.actionsContainer}>
					<DifferentFacilityBookingWarning
						facility={facility}
						defaultFacilityId={household?.default_facility_id}
						style={{ marginBottom: 16 }}
					/>
					<NoFacilityCardWarning
						facilityName={facilityCardStatus.facilityName || facility?.name}
						hasPaymentMethod={facilityCardStatus.hasPaymentMethod}
						loading={facilityCardStatus.loading}
						style={{ marginBottom: 16 }}
					/>
					<PaymentMethodCard
						raised={false}
						household={household}
						facility={facility}
						paymentMethods={facilityCardStatus.paymentMethods}
						onCardSaved={facilityCardStatus.refresh}
					/>
					<div className={classes.actionButtonContainer}>
						<MainButton
							onClick={handleOnConfirmAndBook}
							disabled={facilityCardStatus.loading || !hasCardAtThisFacility}
							variant="contained"
							label="Confirm & Book"
						/>
						<div className={classes.disclaimerText}>
							By clicking "Confirm & Book," I agree that I have notified the client of the quoted
							price,{' '}
							<ButtonBase
								className={classes.inlineButtons}
								onClick={handleDemattingPolicyDialogState}>
								dematting terms and conditions
							</ButtonBase>
							, and
							<ButtonBase
								className={classes.inlineButtons}
								onClick={handleCancellationPolicyDialogState}>
								cancellation policy
							</ButtonBase>
							.
						</div>
						{showBankCreditDisclaimer ? (
							<StyledAlert
								type="info"
								text="Please note the quoted service price may be adjusted if the client's account credit is used or their enrichment bank is used prior to the completion of this visit."
								style={{ marginTop: 8 }}
							/>
						) : null}
					</div>
				</div>
			</div>
		</SectionFormContainer>
	)
}

export default ServiceSummaryAndPayment
